import React, { useState, useEffect, useRef } from "react";
import { toast } from "@/lib/toast";
import {
  CalendarDays,
  RefreshCw,
  CloudOff,
  CheckCircle,
  LogOut,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  UserRound,
  ShieldAlert,
  Smartphone,
  AlertTriangle,
  Trash2,
  ArrowDownToLine,
  Sun,
  SlidersHorizontal,
  Bug,
  Info,
  Crown,
  BookOpenCheck,
  Bell,
  LayoutGrid,
} from "lucide-react";
import { savePushSubscription, deletePushSubscription } from "../lib/api";
import { logout } from "../lib/auth";
import { useDistrict } from "../lib/district-context";
import { useOnlineSync } from "../hooks/useOnlineSync";
import TopBar from "../components/TopBar";
import { useConfirm } from "../components/ConfirmDialog";
import PageHero from "../components/shared/PageHero";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";

import { SettingsListsContent }    from "./SettingsLists";
import { SettingsPlanContent }     from "./SettingsPlan";
import { SettingsSupportContent }  from "./SettingsSupport";
import { SettingsAboutContent }    from "./SettingsAbout";
import { SettingsBillingContent }  from "./SettingsBilling";
import { SettingsModulesContent }  from "./SettingsModules";
import { useReadonly }             from "../hooks/useReadonly";
import { getOpenReportsCount }    from "../lib/bug-reports";
import { useNotificationsCtx }    from "../contexts/NotificationsContext";
import { GUIDES }                 from "../data/guides";
import GuideSheet                 from "../components/GuideSheet";

const THEME_OPTIONS = [
  { value: "light",  label: "Claro"   },
  { value: "dark",   label: "Oscuro"  },
  { value: "system", label: "Sistema" },
];

const DRAWER_ROUTES = {
  "/settings/lists":   "lists",
  "/settings/plan":    "plan",
  "/settings/billing": "billing",
  "/settings/support": "support",
  "/settings/about":   "about",
  "/settings/modules": "modules",
};

const DRAWER_TITLES = {
  lists:   "Listas y categorías",
  plan:    "Horario pastoral",
  billing: "Suscripción",
  support: "Ayuda",
  about:   "Acerca de Kéves",
  modules: "Módulos",
};

// Detect if the app is already running as an installed PWA.
// Stable across a session — no reactivity needed.
const IS_RUNNING_INSTALLED =
  Boolean(window.matchMedia?.("(display-mode: standalone)").matches) ||
  Boolean(navigator.standalone);

const IS_IOS     = /iP(hone|od|ad)/.test(navigator.userAgent);
const IS_ANDROID = /Android/.test(navigator.userAgent);

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  const output  = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
      {children}
    </p>
  );
}

// NavRow — standard navigable row.
// disabled: renders non-interactive (no chevron, muted label, no active feedback).
// badge: optional number — renders a small count bubble between label and chevron.
function NavRow({ icon: Icon, iconBg, iconColor, label, subtitle, onClick, disabled, badge }) {
  const base = "w-full flex items-center gap-3 px-4 py-3.5 text-left";
  const interactive = "transition-all duration-150 active:bg-slate-50 dark:active:bg-slate-700/30 active:scale-[0.98]";

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`${base} ${disabled ? "cursor-default" : interactive}`}
    >
      <div className={`h-10 w-10 rounded-[12px] flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon size={17} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[14px] font-medium ${disabled ? "text-slate-400 dark:text-slate-500" : "text-slate-900 dark:text-slate-100"}`}>
          {label}
        </p>
        {subtitle && (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {badge > 0 && (
          <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
        {!disabled && (
          <ChevronRight size={15} className="text-slate-300 dark:text-slate-600" />
        )}
      </div>
    </button>
  );
}

export default function Settings({ inDrawer = false, onClose, confirm: externalConfirm, backgroundRoute, backgroundScreenshot }) {
  const { district, userProfile, isCoordinator, isAdmin, isPastor } = useDistrict();
  const { theme, setTheme } = useTheme();
  const {
    isOnline, pendingCount, outboxStatus,
    isSyncing, forceSync, retryFailed, purgeFailed,
  } = useOnlineSync();
  const [internalConfirm, InternalConfirmUI] = useConfirm();
  const confirm = externalConfirm ?? internalConfirm;
  const nav = useNavigate();
  const { plan, isFreePlan } = useReadonly();

  const visibleGuides = GUIDES.filter(g =>
    g.role === "all" || (isCoordinator ? g.role === "coordinator" : g.role === "pastor")
  );

  const [drawerView, setDrawerView] = useState("root");
  const [activeGuide, setActiveGuide] = useState(null);
  const [guidesExpanded, setGuidesExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded]   = useState(false);
  const [canInstall, setCanInstall] = useState(Boolean(window.__kevesInstallPrompt));
  const [updating, setUpdating] = useState(false);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [reportsCount, setReportsCount] = useState(null);

  // Push notifications
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSub,     setPushSub]     = useState(null);
  const [pushLoading, setPushLoading] = useState(false);

  // Suppress the version badge on the first mount after a successful update.
  // Set synchronously from localStorage so both version-check effects see it
  // before they run — no async race between effects.
  const suppressVersionCheckRef = useRef(
    Boolean(localStorage.getItem('keves_just_updated'))
  );

  // Clear the flag immediately after reading it (runs before any fetch).
  useEffect(() => {
    if (suppressVersionCheckRef.current) {
      localStorage.removeItem('keves_just_updated');
    }
  }, []);

  useEffect(() => {
    const check = () => setCanInstall(Boolean(window.__kevesInstallPrompt));
    window.addEventListener("beforeinstallprompt", check);
    return () => window.removeEventListener("beforeinstallprompt", check);
  }, []);

  useEffect(() => {
    if (userProfile?.role !== "admin") return;
    getOpenReportsCount().then(setReportsCount).catch(() => {});
  }, [userProfile?.role]);

  useEffect(() => {
    if (!isOnline || suppressVersionCheckRef.current) return;
    fetch("/version.json", { cache: "no-store" })
      .then((r) => r.json())
      .then(({ version: v }) => {
        if (v && v !== process.env.REACT_APP_VERSION) setNewVersionAvailable(true);
      })
      .catch(() => {});
  }, [isOnline]);

  // Also detect when a new SW activates mid-session (race: version.json fetch
  // may run before the new SW installs, so we re-check on NEW_VERSION message).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onSWMessage = (event) => {
      if (event.data?.type !== "NEW_VERSION") return;
      if (suppressVersionCheckRef.current) return;
      fetch("/version.json", { cache: "no-store" })
        .then((r) => r.json())
        .then(({ version: v }) => {
          if (v && v !== process.env.REACT_APP_VERSION) setNewVersionAvailable(true);
        })
        .catch(() => {});
    };
    navigator.serviceWorker.addEventListener("message", onSWMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onSWMessage);
  }, []);

  const navigate = (path) => {
    if (inDrawer) {
      if (DRAWER_ROUTES[path]) { setDrawerView(DRAWER_ROUTES[path]); return; }
      if (onClose) onClose();
      nav(path);
    } else {
      nav(path);
    }
  };

  const handleLogout = async () => {
    const hasPending = isSyncing || pendingCount > 0;
    const ok = await confirm({
      title: "¿Cerrar sesión?",
      description: hasPending
        ? `Tienes ${pendingCount} cambio${pendingCount !== 1 ? "s" : ""} sin sincronizar. Se perderán si cierras sesión ahora.`
        : "Tendrás que volver a iniciar sesión.",
      confirmLabel: "Cerrar sesión",
    });
    if (!ok) return;
    try {
      await logout();
      toast.success("Sesión cerrada correctamente");
    } catch {
      toast.error("Error al cerrar sesión");
    }
  };

  const handleSync = async () => {
    await forceSync();
  };

  const handleUpdateApp = async () => {
    if (!navigator.onLine) {
      toast.error("Necesitas conexión para actualizar la app.");
      return;
    }
    if (isSyncing || pendingCount > 0 || (outboxStatus?.failed || 0) > 0) {
      toast.error("Completa la sincronización pendiente antes de actualizar.");
      return;
    }
    const ok = await confirm({
      title: "¿Actualizar Kéves?",
      description: "Se descargará la versión más reciente. Tus datos no se borran.",
      confirmLabel: "Actualizar ahora",
    });
    if (!ok) return;
    setUpdating(true);
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      localStorage.setItem('keves_just_updated', '1');
      window.location.reload(true);
    } catch {
      localStorage.setItem('keves_just_updated', '1');
      window.location.reload(true);
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateButton = async () => {
    if (newVersionAvailable) return handleUpdateApp();
    if (!navigator.onLine) {
      toast.error("Necesitas conexión para buscar actualizaciones.");
      return;
    }
    if (isSyncing || pendingCount > 0 || (outboxStatus?.failed || 0) > 0) {
      toast.error("Completa la sincronización pendiente antes de actualizar.");
      return;
    }
    setUpdating(true);
    try {
      // Unregister all SWs so the browser fetches sw.js fresh on next load.
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      // Wipe every cache entry so no stale JS/CSS/HTML is served from cache.
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      localStorage.setItem('keves_just_updated', '1');
      window.location.reload(true);
    } catch {
      localStorage.setItem('keves_just_updated', '1');
      window.location.reload(true);
    }
  };

  const handleInstall = async () => {
    const prompt = window.__kevesInstallPrompt;
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      window.__kevesInstallPrompt = null;
      setCanInstall(false);
    }
  };

  const { pendingAccessRequestsCount } = useNotificationsCtx();

  // Detect existing push subscription on mount
  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission !== 'granted') return;
    navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub => { if (sub) { setPushEnabled(true); setPushSub(sub); } })
      .catch(() => {});
  }, []);

  const handlePushToggle = async () => {
    if (!('Notification' in window) || !('PushManager' in window)) {
      toast.error("Tu navegador no soporta notificaciones push");
      return;
    }
    if (pushLoading) return;
    setPushLoading(true);
    try {
      if (!pushEnabled) {
        if (!district?.id && process.env.NODE_ENV !== 'production') console.warn('[Push] district not loaded yet — district_id will be null');
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') { toast.error("Permiso denegado"); return; }
        const reg  = await navigator.serviceWorker.ready;
        const opts = { userVisibleOnly: true };
        const vapid = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        if (vapid) opts.applicationServerKey = urlBase64ToUint8Array(vapid);
        const sub = await reg.pushManager.subscribe(opts);
        await savePushSubscription(sub, district?.id ?? null);
        setPushEnabled(true);
        setPushSub(sub);
        toast.success("Notificaciones activadas");
      } else {
        if (pushSub) await pushSub.unsubscribe();
        await deletePushSubscription().catch(() => {});
        setPushEnabled(false);
        setPushSub(null);
        toast.success("Notificaciones desactivadas");
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('[Push] handlePushToggle error:', err);
      toast.error("No se pudo cambiar las notificaciones");
    } finally {
      setPushLoading(false);
    }
  };

  const hasFailed = (outboxStatus?.failed || 0) > 0;
  const version = process.env.REACT_APP_VERSION;
  const isSyncedAndOnline = isOnline && pendingCount === 0 && !hasFailed;
  const systemAllGood = isSyncedAndOnline && !newVersionAvailable;

  // ── DRAWER SUB-VIEW ─────────────────────────────────────────────────────────
  if (inDrawer && drawerView !== "root") {
    return (
      <div className="font-sans flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200/60 dark:border-slate-700/60 sticky top-0 bg-page-bg/95 backdrop-blur-sm z-10 shrink-0">
          <button
            onClick={() => setDrawerView("root")}
            aria-label="Volver a ajustes"
            className="h-8 w-8 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors duration-150 shrink-0"
          >
            <ChevronLeft size={17} />
          </button>
          <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">
            {DRAWER_TITLES[drawerView]}
          </p>
        </div>

        <div className="anim-in flex-1 overflow-y-auto overscroll-contain">
          {drawerView === "lists"   && <SettingsListsContent />}
          {drawerView === "plan"    && <SettingsPlanContent confirm={confirm} />}
          {drawerView === "billing" && !isCoordinator && <SettingsBillingContent />}
          {drawerView === "support" && (
            <SettingsSupportContent
              backgroundRoute={backgroundRoute}
              backgroundScreenshot={backgroundScreenshot}
            />
          )}
          {drawerView === "about"   && <SettingsAboutContent />}
          {drawerView === "modules" && (isAdmin || isPastor) && <SettingsModulesContent />}
        </div>
      </div>
    );
  }

  // ── MAIN SETTINGS VIEW ──────────────────────────────────────────────────────
  return (
    <div className={inDrawer
      ? "font-sans"
      : "anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))] font-sans"
    }>
      {!externalConfirm && InternalConfirmUI}

      {!inDrawer && (
        <TopBar title="Ajustes" safeFallback="/" />
      )}

      <div className="px-4 py-5 space-y-4">

        {/* ── VERSÍCULO ─────────────────────────────────────────── */}
        <PageHero
          title={'"El Señor es mi pastor; tengo todo lo que necesito."'}
          subtitle="Salmos 23:1 (NTV)"
          className="px-5 py-4"
        />

        {/* ── MI CUENTA ─────────────────────────────────────────── */}
        <section className="space-y-2">
          <SectionLabel>Mi cuenta</SectionLabel>
          <div className="card-soft overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/50">

            <NavRow
              icon={UserRound}
              iconBg="bg-brand-light dark:bg-brand/15"
              iconColor="text-brand"
              label={userProfile?.full_name || "Perfil"}
              subtitle={district?.name || "Información personal"}
              onClick={() => navigate("/profile")}
            />

            {!isCoordinator && (
              <NavRow
                icon={Crown}
                iconBg={isFreePlan ? "bg-slate-100 dark:bg-slate-700" : "bg-emerald-50 dark:bg-emerald-900/20"}
                iconColor={isFreePlan ? "text-slate-400 dark:text-slate-500" : "text-emerald-600 dark:text-emerald-400"}
                label="Suscripción"
                subtitle={isFreePlan ? "Plan Gratuito — solo lectura" : "Plan Pro activo"}
                onClick={() => navigate("/settings/billing")}
              />
            )}

            {/* Apariencia — control inline */}
            <div className="px-4 py-3.5 flex items-start gap-3">
              <div className="h-10 w-10 rounded-[12px] bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
                <Sun size={17} className="text-slate-500 dark:text-slate-400" />
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Apariencia</p>
                <div className="flex gap-1 mt-2">
                  {THEME_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setTheme(opt.value)}
                      className={`flex-1 py-1 rounded-[10px] text-[11px] font-semibold transition-all duration-200 active:scale-[0.97]
                        ${theme === opt.value
                          ? "bg-brand text-white shadow-sm"
                          : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600"
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ── PERSONALIZACIÓN ───────────────────────────────────── */}
        <section className="space-y-2">
          <SectionLabel>Personalización</SectionLabel>
          <div className="card-soft overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/50">
            <NavRow
              icon={SlidersHorizontal}
              iconBg="bg-violet-50 dark:bg-violet-900/20"
              iconColor="text-violet-600 dark:text-violet-400"
              label="Listas y categorías"
              subtitle="Tipos, estados y categorías"
              onClick={() => navigate("/settings/lists")}
            />
            <NavRow
              icon={CalendarDays}
              iconBg="bg-blue-50 dark:bg-blue-900/20"
              iconColor="text-blue-600 dark:text-blue-400"
              label="Horario pastoral"
              subtitle="Semana y rutina diaria"
              onClick={() => navigate("/settings/plan")}
            />
            {(isAdmin || isPastor) && (
              <NavRow
                icon={LayoutGrid}
                iconBg="bg-emerald-50 dark:bg-emerald-900/20"
                iconColor="text-emerald-600 dark:text-emerald-400"
                label="Módulos"
                subtitle="Activa o desactiva secciones del distrito"
                onClick={() => navigate("/settings/modules")}
              />
            )}
          </div>
        </section>

        {/* ── SISTEMA ───────────────────────────────────────────── */}
        <section className="space-y-2">
          <SectionLabel>Sistema</SectionLabel>

          {systemAllGood ? (
            <div className="card-soft px-4 py-3.5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-[12px] bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
                <CheckCircle size={17} className="text-emerald-500 dark:text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Todo al día</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Sin cambios pendientes{version ? ` · v${version}` : ""}
                </p>
              </div>
            </div>
          ) : (
          <div className="card-soft overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/50">

            {/* Sincronización — hidden when fully synced to avoid redundancy with the
                version row ("Kéves está al día") that always renders below it. */}
            {!isSyncedAndOnline && (
              <div className="px-4 py-3.5 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-[12px] flex items-center justify-center shrink-0 ${
                  isOnline ? "bg-amber-50 dark:bg-amber-900/20" : "bg-slate-100 dark:bg-slate-700"
                }`}>
                  {isOnline
                    ? <RefreshCw size={17} className={`text-amber-500 ${isSyncing ? "animate-spin" : ""}`} />
                    : <CloudOff size={17} className="text-slate-400" />
                  }
                </div>
                <p className="flex-1 text-[14px] font-medium text-slate-900 dark:text-slate-100">
                  {!isOnline
                    ? "Sin conexión"
                    : isSyncing
                    ? "Sincronizando…"
                    : `${pendingCount} cambio${pendingCount !== 1 ? "s" : ""} pendiente${pendingCount !== 1 ? "s" : ""}`
                  }
                </p>
                {isOnline && pendingCount > 0 && !isSyncing && (
                  <button
                    onClick={handleSync}
                    disabled={isSyncing}
                    className="h-9 px-3 rounded-[10px] text-[12px] font-semibold bg-brand text-white flex items-center gap-1.5 transition-all duration-200 active:scale-[0.98] active:opacity-80 disabled:opacity-50"
                  >
                    <RefreshCw size={12} />
                    Sincronizar
                  </button>
                )}
              </div>
            )}

            {/* Instalar — siempre visible, 3 estados */}
            {IS_RUNNING_INSTALLED ? (
              // Estado: ya instalada como PWA
              <div className="px-4 py-3.5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-[12px] bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
                  <Smartphone size={17} className="text-slate-400 dark:text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-500 dark:text-slate-400">Instalada en pantalla de inicio</p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">Ya está en tu dispositivo</p>
                </div>
                <CheckCircle size={15} className="text-emerald-400 shrink-0" />
              </div>
            ) : canInstall ? (
              // Estado: disponible para instalar
              <button
                onClick={handleInstall}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-150 active:bg-slate-50 dark:active:bg-slate-700/30 active:scale-[0.98]"
              >
                <div className="h-10 w-10 rounded-[12px] bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                  <Smartphone size={17} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Instalar en pantalla de inicio</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Disponible para este dispositivo</p>
                </div>
                <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 shrink-0" />
              </button>
            ) : IS_IOS ? (
              // Estado: iOS Safari — instrucciones manuales via share sheet
              <div className="px-4 py-3.5 flex items-start gap-3">
                <div className="h-10 w-10 rounded-[12px] bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Smartphone size={17} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Agregar a pantalla de inicio</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    Toca <span className="font-semibold">Compartir ⬆</span> → "Agregar a inicio"
                  </p>
                </div>
              </div>
            ) : IS_ANDROID ? (
              // Estado: Android sin prompt — instrucciones manuales via menú
              <div className="px-4 py-3.5 flex items-start gap-3">
                <div className="h-10 w-10 rounded-[12px] bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Smartphone size={17} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Agregar a pantalla de inicio</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    Menú <span className="font-semibold">⋮</span> → "Añadir a pantalla de inicio"
                  </p>
                </div>
              </div>
            ) : (
              // Estado: escritorio u otro navegador
              <div className="px-4 py-3.5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-[12px] bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
                  <Smartphone size={17} className="text-slate-300 dark:text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-400 dark:text-slate-500">Pantalla de inicio</p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">Abre en Chrome o Safari desde móvil</p>
                </div>
              </div>
            )}

          </div>
          )} {/* end systemAllGood else */}

          {/* Versión / Actualización — siempre visible */}
          <div className="card-soft px-4 py-3.5">
            <button
              onClick={handleUpdateButton}
              disabled={updating || isSyncing}
              className="w-full h-11 rounded-[14px] bg-slate-100 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200 text-[13px] font-semibold flex items-center gap-3 px-3.5 transition-all duration-200 active:scale-[0.98] active:opacity-80 disabled:opacity-50"
            >
              {updating
                ? <RefreshCw size={14} className="animate-spin text-brand shrink-0" />
                : newVersionAvailable
                  ? <ArrowDownToLine size={14} className="text-brand shrink-0" />
                  : <RefreshCw size={14} className="text-slate-500 dark:text-slate-400 shrink-0" />
              }
              <span className="flex-1 text-left">
                {updating ? "Actualizando…" : "Actualizar Kéves"}
              </span>
              {newVersionAvailable ? (
                <span className="flex items-center gap-1 bg-brand/10 dark:bg-brand/20 text-brand dark:text-blue-400 text-[10px] font-bold px-2 py-1 rounded-full leading-none">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand dark:bg-blue-400 shrink-0" />
                  Nueva versión
                </span>
              ) : (
                version && (
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    v{version}
                  </span>
                )
              )}
            </button>
          </div>

          {/* Notificaciones push */}
          {'Notification' in window && 'PushManager' in window && (() => {
            const permDenied = Notification.permission === 'denied';
            return (
              <div className="card-soft px-4 py-3.5 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-[12px] flex items-center justify-center shrink-0 transition-colors duration-200 ${
                  pushEnabled ? "bg-blue-50 dark:bg-blue-900/20" : "bg-slate-100 dark:bg-slate-700"
                }`}>
                  <Bell size={17} className={pushEnabled ? "text-brand dark:text-blue-400" : "text-slate-400 dark:text-slate-500"} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Notificaciones push</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {permDenied
                      ? "Bloqueadas — actívalas en Ajustes del sistema"
                      : "Avisos cuando lleguen mensajes"}
                  </p>
                </div>
                <button
                  onClick={handlePushToggle}
                  disabled={pushLoading || permDenied}
                  aria-label={pushEnabled ? "Desactivar notificaciones" : "Activar notificaciones"}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-40 ${
                    pushEnabled ? "bg-brand" : "bg-slate-200 dark:bg-slate-600"
                  }`}
                >
                  <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                    pushEnabled ? "translate-x-5" : "translate-x-0.5"
                  }`} />
                </button>
              </div>
            );
          })()}

          {/* Error de sync — solo cuando hay fallos */}
          {hasFailed && (
            <div className="rounded-[20px] bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 p-4 space-y-3">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-red-800 dark:text-red-300">
                    {outboxStatus.failed} {outboxStatus.failed === 1 ? "cambio no guardado" : "cambios no guardados"}
                  </p>
                  <p className="text-[11px] text-red-500 dark:text-red-400 mt-0.5">
                    No se pudieron sincronizar con el servidor.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={retryFailed}
                  disabled={!isOnline || isSyncing}
                  className="flex-1 h-9 rounded-[12px] text-[12px] font-semibold bg-red-600 text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                >
                  Reintentar
                </button>
                <button
                  onClick={purgeFailed}
                  disabled={isSyncing}
                  className="h-11 w-11 rounded-[12px] flex items-center justify-center bg-red-100 dark:bg-red-900/30 text-red-500 transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                  title="Eliminar cambios fallidos"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── GUÍAS DE USO ──────────────────────────────────────── */}
        <section className="space-y-2">
          <div className="card-soft overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/50">
            <button
              onClick={() => setGuidesExpanded(v => !v)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-150 active:bg-slate-50 dark:active:bg-slate-700/30 active:scale-[0.98]"
            >
              <div className="h-10 w-10 rounded-[12px] bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center shrink-0">
                <BookOpenCheck size={17} className="text-sky-600 dark:text-sky-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Guías de uso</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{visibleGuides.length} guías disponibles</p>
              </div>
              <ChevronDown
                size={15}
                className={`text-slate-300 dark:text-slate-600 shrink-0 transition-transform duration-200 ${guidesExpanded ? "rotate-180" : ""}`}
              />
            </button>
            {guidesExpanded && visibleGuides.map((guide) => {
              const Icon = guide.icon;
              return (
                <button
                  key={guide.id}
                  onClick={() => setActiveGuide(guide)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 active:bg-slate-50 dark:active:bg-slate-700/30 active:scale-[0.98]"
                >
                  <div className={`h-9 w-9 rounded-[12px] flex items-center justify-center shrink-0 ${guide.iconBg}`}>
                    <Icon size={17} className={guide.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">{guide.title}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">{guide.description}</p>
                  </div>
                  <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 shrink-0" />
                </button>
              );
            })}
          </div>
        </section>

        {activeGuide && (
          <GuideSheet guide={activeGuide} onClose={() => setActiveGuide(null)} />
        )}

        {/* ── AYUDA ─────────────────────────────────────────────── */}
        <section className="space-y-2">
          <SectionLabel>Ayuda</SectionLabel>
          <div className="card-soft overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/50">
            <NavRow
              icon={Bug}
              iconBg="bg-rose-50 dark:bg-rose-900/20"
              iconColor="text-rose-500 dark:text-rose-400"
              label="Algo no funciona"
              subtitle="Describe qué ocurrió"
              onClick={() => navigate("/settings/support")}
            />
            <NavRow
              icon={Info}
              iconBg="bg-slate-100 dark:bg-slate-700"
              iconColor="text-slate-500 dark:text-slate-400"
              label="Acerca de Kéves"
              subtitle="Historia, filosofía y versión"
              onClick={() => navigate("/settings/about")}
            />
          </div>
        </section>

        {/* ── ADMINISTRACIÓN — solo admin ────────────────────────── */}
        {userProfile?.role === "admin" && (
          <section className="space-y-2">
            <div className="card-soft overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/50">
              <button
                onClick={() => setAdminExpanded(v => !v)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-150 active:bg-slate-50 dark:active:bg-slate-700/30 active:scale-[0.98]"
              >
                <div className="h-10 w-10 rounded-[12px] bg-brand-light dark:bg-brand/15 flex items-center justify-center shrink-0">
                  <ShieldAlert size={17} className="text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">Administración</p>
                  {(pendingAccessRequestsCount > 0 || reportsCount > 0) && (
                    <p className="text-[11px] text-rose-500 dark:text-rose-400 mt-0.5">Hay items pendientes de atención</p>
                  )}
                </div>
                {(pendingAccessRequestsCount > 0) && (
                  <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shrink-0">
                    {pendingAccessRequestsCount > 9 ? "9+" : pendingAccessRequestsCount}
                  </span>
                )}
                <ChevronDown
                  size={15}
                  className={`text-slate-300 dark:text-slate-600 shrink-0 transition-transform duration-200 ${adminExpanded ? "rotate-180" : ""}`}
                />
              </button>
              {adminExpanded && (
                <>
                  <NavRow
                    icon={ShieldAlert}
                    iconBg="bg-brand-light dark:bg-brand/15"
                    iconColor="text-brand"
                    label="Gestionar usuarios"
                    subtitle="Aprobar pastores y solicitudes"
                    badge={pendingAccessRequestsCount}
                    onClick={() => navigate("/admin/users")}
                  />
                  <NavRow
                    icon={Bug}
                    iconBg="bg-emerald-50 dark:bg-emerald-900/20"
                    iconColor="text-emerald-600 dark:text-emerald-400"
                    label={`Comentarios y errores${reportsCount ? ` (${reportsCount})` : ""}`}
                    subtitle="Reportes enviados por pastores"
                    onClick={() => navigate("/admin/reports")}
                  />
                </>
              )}
            </div>
          </section>
        )}

        {/* ── CERRAR SESIÓN ─────────────────────────────────────── */}
        <section className="pt-2">
          <button
            onClick={handleLogout}
            className="w-full h-11 rounded-[20px] bg-red-500 text-white text-[14px] font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] active:opacity-80 shadow-sm"
          >
            <LogOut size={16} />
            Cerrar sesión
          </button>
        </section>

        {/* ── FOOTER ────────────────────────────────────────────── */}
        <p className="text-center text-[10px] text-slate-400/70 pb-2">
          Hecho con cuidado pastoral
        </p>

      </div>
    </div>
  );
}
